import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';
import { generateHardDiskId } from '../utils/hardwareId';
import { refreshDeviceLocation } from '../utils/deviceLocation';
import { getApiBaseUrl } from '../services/apiConfig';
import {
  isLicenseCacheStillValid,
  readLicenseValidationCache,
  shouldRefreshLicenseCache,
  writeLicenseValidationCache,
  type LicenseValidationCache,
} from '../utils/licenseValidationCache';

/** تفعيل بوابة الترخيص: الجهاز غير المرخّص يعرض رقمه حتى يُدخل كود التفعيل. */
export const LICENSE_ENFORCEMENT_DISABLED = false

/** تحديث مكان استخدام الإنترنت كل دقيقتين (من خروج الجهاز نفسه) */
const LOCATION_REPORT_INTERVAL_MS = 30 * 60 * 1000;

export interface LicenseStatus {
  isValid: boolean;
  remainingDays: number;
  expiryDate: string;
  daysCount: number;
  startDate: string;
  companyName?: string;
  licenseName?: string;
  approvalStatus?: string;
  message?: string;
  licenseId?: number;
  lastSuccessfulValidation?: string;
  nextValidationTime?: string;
  fromCache?: boolean;
  validationIntervalHours?: number;
}

function hasApiBaseUrl(): boolean {
  try {
    return !!getApiBaseUrl();
  } catch {
    return false;
  }
}

function toLicenseStatus(cache: LicenseValidationCache): LicenseStatus {
  return {
    isValid: cache.isValid,
    remainingDays: cache.remainingDays ?? 0,
    expiryDate: cache.expiryDate ?? '',
    daysCount: cache.daysCount ?? 0,
    startDate: cache.startDate ?? '',
    companyName: cache.companyName,
    licenseName: cache.licenseName,
    approvalStatus: cache.approvalStatus || cache.status,
    message: cache.message,
    licenseId: cache.licenseId,
    lastSuccessfulValidation: cache.lastSuccessfulValidation,
    nextValidationTime: cache.nextValidationTime,
    fromCache: true,
    validationIntervalHours: cache.validationIntervalHours,
  };
}

function persistStatus(hardDiskId: string, status: LicenseStatus): void {
  writeLicenseValidationCache({
    licenseId: status.licenseId,
    hardDiskId,
    status: status.approvalStatus || '',
    isValid: status.isValid,
    remainingDays: status.remainingDays,
    expiryDate: status.expiryDate,
    daysCount: status.daysCount,
    startDate: status.startDate,
    companyName: status.companyName,
    licenseName: status.licenseName,
    approvalStatus: status.approvalStatus,
    message: status.message,
    lastSuccessfulValidation: status.lastSuccessfulValidation,
    nextValidationTime: status.nextValidationTime,
    validationIntervalHours: status.validationIntervalHours,
  });
}

async function fetchActiveLicense(hardDiskId: string): Promise<LicenseStatus> {
  const response = await apiClient.get(API_ENDPOINTS.license.active, {
    params: { hardDiskId },
    timeout: 8000,
  });
  return response.data;
}

async function reportCurrentLocation(hardDiskId: string): Promise<void> {
  try {
    const location = await refreshDeviceLocation();
    await apiClient.put(API_ENDPOINTS.license.updateLocation, {
      hardDiskId,
      deviceLocation: location.display,
      latitude: location.latitude,
      longitude: location.longitude,
      city: location.city,
      country: location.country,
      ipAddress: location.ipAddress,
    }, { timeout: 8000 });
  } catch {
    // لا نعطّل التطبيق إذا فشل تحديث الموقع
  }
}

export function useLicenseStatus(enabled = true) {
  const queryEnabled = !LICENSE_ENFORCEMENT_DISABLED && enabled && hasApiBaseUrl();
  const hardDiskId = queryEnabled ? generateHardDiskId() : '';
  const cached = hardDiskId ? readLicenseValidationCache(hardDiskId) : null;
  const cachedStatus = cached ? toLicenseStatus(cached) : undefined;
  const cacheUsable = isLicenseCacheStillValid(cached);
  const cacheDue = shouldRefreshLicenseCache(cached);

  const { data: licenseStatus, isLoading, error } = useQuery<LicenseStatus>({
    queryKey: ['licenseStatus', hardDiskId],
    enabled: queryEnabled && !!hardDiskId,
    initialData: cacheUsable ? cachedStatus : undefined,
    queryFn: async () => {
      const currentHardDiskId = generateHardDiskId();
      const previous = readLicenseValidationCache(currentHardDiskId);
      try {
        const existing = await fetchActiveLicense(currentHardDiskId);
        persistStatus(currentHardDiskId, existing);
        return existing;
      } catch {
        if (previous && isLicenseCacheStillValid(previous)) {
          return toLicenseStatus(previous);
        }
        return {
          isValid: false,
          remainingDays: 0,
          expiryDate: '',
          daysCount: 0,
          startDate: '',
          approvalStatus: 'None',
          message: 'لا يوجد ترخيص نشط',
        };
      }
    },
    staleTime: cacheUsable && !cacheDue ? 24 * 60 * 60 * 1000 : 0,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnMount: cacheUsable && !cacheDue ? false : 'always',
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const status = query.state.data?.approvalStatus;
      const valid = query.state.data?.isValid === true;
      if (valid && status === 'Approved') return false;
      if (status === 'Pending') return 5 * 60 * 1000;
      return false;
    },
    retry: 0,
  });

  useEffect(() => {
    if (!queryEnabled || licenseStatus?.approvalStatus !== 'Approved' || !licenseStatus?.isValid) return;

    const id = generateHardDiskId();
    void reportCurrentLocation(id);

    const timer = window.setInterval(() => {
      void reportCurrentLocation(id);
    }, LOCATION_REPORT_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [queryEnabled, licenseStatus?.approvalStatus, licenseStatus?.isValid]);

  const isApproved = LICENSE_ENFORCEMENT_DISABLED || licenseStatus?.approvalStatus === 'Approved';
  const isPending = !LICENSE_ENFORCEMENT_DISABLED && licenseStatus?.approvalStatus === 'Pending';
  const isRejected = !LICENSE_ENFORCEMENT_DISABLED && licenseStatus?.approvalStatus === 'Rejected';
  const isSuspended = !LICENSE_ENFORCEMENT_DISABLED && licenseStatus?.approvalStatus === 'Suspended';
  const isDeleted = !LICENSE_ENFORCEMENT_DISABLED && licenseStatus?.approvalStatus === 'Deleted';
  const isExpired =
    !LICENSE_ENFORCEMENT_DISABLED &&
    !licenseStatus?.isValid &&
    licenseStatus?.approvalStatus === 'Approved';
  const hasNoLicense =
    !LICENSE_ENFORCEMENT_DISABLED &&
    (!licenseStatus || licenseStatus.approvalStatus === 'None');

  const canAccess =
    LICENSE_ENFORCEMENT_DISABLED || (isApproved && licenseStatus?.isValid === true);

  return {
    licenseStatus,
    isLoading: LICENSE_ENFORCEMENT_DISABLED ? false : (cacheUsable ? false : isLoading),
    error,
    isApproved,
    isPending,
    isRejected,
    isSuspended,
    isDeleted,
    isExpired,
    hasNoLicense,
    canAccess,
  };
}
